import { Box, CircularProgress } from "@mui/material"
import { useEffect, useState } from "react"
import {
    Board,
    ConfirmCommitmentStatusModal,
    CreateOpportunitiesModal,
    DeleteObjectiveModal,
    EditObjectiveModal,
    Drawer,
    Header,
    ImpactedClientsModal,
    MainContent,
    RecommendationDetailsModal,
    RelatedOpportunitiesModal,
} from "src/lib"
import { LateralChat, OverviewSection } from "src/parts"
import { CommitmentsSection } from "src/parts/CommitmentsSection"
import { ExecutionPerformanceSection } from "src/parts/ExecutionPerformanceSection"
import { PrioritizationCriteriaSection } from "src/parts/CommitmentsSection/PrioritizationCriteriaSection"
import { OpportunitiesSection } from "src/parts/OpportunitiesSection"
import { getUserHistoryMessages } from "src/services/api/chat"
import { intl } from "src/internationalization/intl"
import { isManagerProfile } from "src/lib/utils/isManagerProfile"
import { useMessageStore } from "src/store/messageStore"
import { useModalStore } from "src/store/modalStore"
import { useUserStore } from "src/store/userStore"

interface IAdvisorDashboardProps {
    managerTab: "advisor" | "manager"
}

const AdvisorDashboard = ({ managerTab }: IAdvisorDashboardProps) => {
    const openModal = useModalStore((s: any) => s.open)
    const payload = useModalStore((s: any) => s.payload)
    const closeModal = useModalStore((s: any) => s.closeModal)
    const { user, tokens } = useUserStore();
    const isManager = isManagerProfile(user?.profile);

    const panelTitle = isManager
        ? intl(managerTab === "manager" ? "managerPanelTitle" : "panelTitle")
        : intl("panelTitle");
    const loadHistoryMessages = useMessageStore((s: any) => s.loadHistoryMessages);
    const [historyLoaded, setHistoryLoaded] = useState(false);

    useEffect(() => {
        const loadHistory = async () => {
            if (tokens.backendToken && !historyLoaded) {
                try {
                    const historyData = await getUserHistoryMessages();
                    if (historyData.history && historyData.history.length > 0) {
                        loadHistoryMessages(historyData.history);
                    }
                    setHistoryLoaded(true);
                } catch (error) {
                    console.error("Erro ao carregar histórico de mensagens:", error);
                    // Não bloqueia a aplicação se houver erro ao carregar histórico
                    setHistoryLoaded(true);
                }
            }
        };

        loadHistory();
    }, [tokens.backendToken, historyLoaded, loadHistoryMessages]);



    // Adicionar um loading estilizado aqui, esse loading foi necessario para evitar chamadas precoces 
    // à API antes do token ser setado na store pelo hook useAuthSync
    if (!tokens.backendToken) {
        return (
            <Box
                sx={{
                    display: "flex",
                    height: "100vh",
                    width: "100vw",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <CircularProgress size={80} thickness={4} />
            </Box>
        );
    }
    return (
        <>
            <Drawer.Root>
                <LateralChat />
            </Drawer.Root>
            <MainContent>
                <Header title={panelTitle} />
                <Board data-testid="board">
                    {!isManager && (
                        <>
                            <OverviewSection />
                            <OpportunitiesSection />
                            <PrioritizationCriteriaSection />
                            <CommitmentsSection hideRanking />
                        </>
                    )}
                    {isManager && (
                        <>
                            <Box sx={{ display: managerTab === "advisor" ? "block" : "none" }}>
                                <OverviewSection />
                                <OpportunitiesSection
                                    showMonitoringPanel={false}
                                    showCreateObjectiveButton={false}
                                />
                                <PrioritizationCriteriaSection />
                                <CommitmentsSection hideRanking />
                            </Box>
                            <Box sx={{ display: managerTab === "manager" ? "block" : "none" }}>
                                <OverviewSection aggregated />
                                <CommitmentsSection rankingOnly />
                                <OpportunitiesSection
                                    showMonitoringPanel
                                    showManageObjectiveActions
                                />
                                <ExecutionPerformanceSection />
                            </Box>
                        </>
                    )}
                </Board>
            </MainContent>
            {openModal === "createOpportunitiesModal" && isManager && (
                <CreateOpportunitiesModal open={true} onClose={closeModal} />
            )}
            {payload && openModal === 'relatedOpportunitiesModal' && (
                <RelatedOpportunitiesModal
                    open={true}
                    client={payload}
                    onClose={closeModal}
                />
            )}
            {payload && openModal === 'recommendationDetailsModal' && (
                <RecommendationDetailsModal
                    open={true}
                    recommendation={payload.recommendation}
                    client={payload}
                    onClose={closeModal}
                />
            )}
            {payload && openModal === 'confirmCommitmentStatusModal' && (
                <ConfirmCommitmentStatusModal
                    open={true}
                    commitmentId={payload.commitmentId}
                    currentStatus={payload.currentStatus}
                    clientName={payload.clientName}
                    accountId={payload.accountId}
                    dueDate={payload.dueDate}
                    onClose={closeModal}
                />
            )}
            {payload && openModal === 'impactedClientsModal' && (
                <ImpactedClientsModal
                    open={true}
                    impactedClients={payload.impactedClients}
                    opportunityTitle={payload.opportunityTitle}
                    recommendation={payload.recommendation}
                    addedAt={payload.addedAt}
                    dueDate={payload.dueDate}
                    impactedClientsCount={payload.impactedClientsCount}
                    onClose={closeModal}
                />
            )}
            {payload && openModal === 'deleteObjectiveModal' && isManager && managerTab === "manager" && (
                <DeleteObjectiveModal
                    open={true}
                    objectiveId={payload.objectiveId}
                    objectiveTitle={payload.objectiveTitle}
                    onClose={closeModal}/>
            )}
            {payload && openModal === 'editObjectiveModal' && isManager && managerTab === "manager" && (
                <EditObjectiveModal
                    open={true}
                    objectiveId={payload.objectiveId}
                    title={payload.title}
                    createdDate={payload.createdDate}
                    endDate={payload.endDate}
                    onClose={closeModal}
                />
            )}

        </>
    )
}

export { AdvisorDashboard }

